/**
 * ProductImagesHeader - Header bar for ProductImagesSubTab
 *
 * Shows title, image/channel count subtitle, size toggle, and the
 * sync / optimize / library / settings actions. M2: subtitle reworked to
 * "N Bilder · M Kanäle · alle Kanäle untereinander", action order aligned
 * to the mockup (Size · Sync · Optimize · Library · Settings) and all
 * user-facing strings localized.
 */

import { CloudDownload, Sparkles, Images, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui'
import type { ProductAsset } from '@/types/products'
import type { ImageSize } from './types'

interface ProductImagesHeaderProps {
  assets: ProductAsset[]
  imageSize: ImageSize
  setImageSize: (size: ImageSize) => void
  syncing: boolean
  saving: boolean
  optimizing: boolean
  isLibraryOpen: boolean
  onSyncAll: () => void
  onViewSettings: (channelId?: string, channelName?: string) => void
  onOptimizeImages: (channelId?: string) => void
  onToggleLibrary: () => void
}

export function ProductImagesHeader({
  assets, imageSize, setImageSize, syncing, saving, optimizing,
  isLibraryOpen, onSyncAll, onViewSettings, onOptimizeImages, onToggleLibrary,
}: ProductImagesHeaderProps) {
  // M1/M2: alle Kanäle untereinander → Kanäle = distinct channel_ids (+ global)
  const channelCount =
    new Set(assets.filter((a) => a.channel_id).map((a) => a.channel_id)).size +
    (assets.some((a) => !a.channel_id) ? 1 : 0)

  return (
    <div className="flex justify-between items-center mb-6 flex-shrink-0">
      <TitleSection total={assets.length} channels={channelCount} />
      <ActionButtons
        imageSize={imageSize} setImageSize={setImageSize}
        syncing={syncing} saving={saving} optimizing={optimizing}
        isLibraryOpen={isLibraryOpen} hasAssets={assets.length > 0}
        onSyncAll={onSyncAll} onViewSettings={() => onViewSettings()}
        onOptimizeImages={() => onOptimizeImages()} onToggleLibrary={onToggleLibrary}
      />
    </div>
  )
}

function TitleSection({ total, channels }: { total: number; channels: number }) {
  const { t } = useTranslation()
  return (
    <div>
      <h6 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
        {t('productImages.header.title')}
      </h6>
      <span className="text-sm text-neutral-600 dark:text-neutral-400">
        {t('productImages.header.subtitle', { images: total, channels })}
      </span>
    </div>
  )
}

function ActionButtons(props: {
  imageSize: ImageSize; setImageSize: (s: ImageSize) => void
  syncing: boolean; saving: boolean; optimizing: boolean
  isLibraryOpen: boolean; hasAssets: boolean
  onSyncAll: () => void; onViewSettings: () => void
  onOptimizeImages: () => void; onToggleLibrary: () => void
}) {
  const { t } = useTranslation()
  const disabled = props.syncing || props.saving || props.optimizing

  return (
    <div className="flex gap-2">
      <SizeToggle imageSize={props.imageSize} setImageSize={props.setImageSize} />
      <SyncButton syncing={props.syncing} disabled={disabled} onClick={props.onSyncAll} />
      <OptimizeButton optimizing={props.optimizing} disabled={disabled || !props.hasAssets} onClick={props.onOptimizeImages} />
      <LibraryButton isOpen={props.isLibraryOpen} disabled={props.syncing || props.saving} onClick={props.onToggleLibrary} />
      <Button variant="ghost" size="sm" onClick={props.onViewSettings} disabled={disabled} title={t('productImages.header.settings')}>
        <Settings className="h-4 w-4" />
      </Button>
    </div>
  )
}

function SizeToggle({ imageSize, setImageSize }: { imageSize: ImageSize; setImageSize: (s: ImageSize) => void }) {
  return (
    <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 p-1 gap-0.5">
      {(['S', 'M', 'L'] as const).map((size) => (
        <Button key={size} variant={imageSize === size ? 'primary' : 'ghost'} size="sm" onClick={() => setImageSize(size)}>
          {size}
        </Button>
      ))}
    </div>
  )
}

function SyncButton({ syncing, disabled, onClick }: { syncing: boolean; disabled: boolean; onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <Button variant="outline" size="sm" onClick={onClick} disabled={disabled}>
      {syncing ? (
        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />{t('productImages.header.syncing')}</>
      ) : (
        <><CloudDownload className="h-4 w-4 mr-2" />{t('productImages.header.sync')}</>
      )}
    </Button>
  )
}

function OptimizeButton({ optimizing, disabled, onClick }: { optimizing: boolean; disabled: boolean; onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <Button variant="outline" size="sm" onClick={onClick} disabled={disabled}>
      {optimizing ? (
        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />{t('productImages.header.optimizing')}</>
      ) : (
        <><Sparkles className="h-4 w-4 mr-2" />{t('productImages.header.optimize')}</>
      )}
    </Button>
  )
}

function LibraryButton({ isOpen, disabled, onClick }: { isOpen: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <Button variant={isOpen ? 'primary' : 'outline'} size="sm" onClick={onClick} disabled={disabled} className="gap-2">
      <Images className="h-4 w-4" />
      Library
    </Button>
  )
}
